import { PredictPatient } from '~/types/predict';

type QualifyChecks = NonNullable<PredictPatient['qualifyChecks']>;

// Social worker names used for the social-stability + consent verifications.
const SOCIAL_WORKERS = [
  'Renee Alvarez, MSW',
  'Marcus Bell, LCSW',
  'Priya Nair, MSW',
  'Tom Becker, LCSW',
];

// How far through the three EHR qualification checks a patient has progressed.
export type QualifyLevel = 'none' | 'clinical_social' | 'all';

/**
 * Build the three EHR-driven qualify checks for a patient, verified up to the
 * given level.
 *
 * Clinical stability is verified 15-45 min before social/consent and by a
 * clinician (the attending physician); social stability + consent share a single
 * social worker and timestamp.
 */
export function buildQualifyChecks(
  patient: Pick<PredictPatient, 'id' | 'attendingPhysician'>,
  baseIso: string,
  level: QualifyLevel,
): QualifyChecks {
  const idHash = [...patient.id].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  const baseMs = new Date(baseIso).getTime();
  const clinicalOffsetMin = 15 + (idHash % 31); // 15-45 minutes earlier
  const clinicalAt = new Date(baseMs - clinicalOffsetMin * 60_000).toISOString();
  const socialWorker = SOCIAL_WORKERS[idHash % SOCIAL_WORKERS.length];

  const unverified = { verified: false };

  return {
    clinicalStability: level === 'none'
      ? unverified
      : { verified: true, verifiedAt: clinicalAt, verifiedBy: patient.attendingPhysician },
    socialStability: level === 'none'
      ? unverified
      : { verified: true, verifiedAt: baseIso, verifiedBy: socialWorker },
    consent: level === 'all'
      ? { verified: true, verifiedAt: baseIso, verifiedBy: socialWorker }
      : unverified,
  };
}
